import React, { Component } from 'react'
import PropTypes from 'prop-types'
import paymentService from 'Services/paymentService'
import InitialPropsHelper from 'Utils/InitialPropsHelper'
import Layout from 'Components/Layout'
import globalsService from 'Services/globalsService'
import courseService from 'Services/courseService'
import AlertBlock from 'Components/AlertBlock'
import CourseReview from 'Components/CourseReview'
import essentialsService from 'Services/essentialsService'
import Router, { withRouter } from 'next/router'
import RatingBlock from 'Components/RatingBlock'
import Button from 'Components/Button'
import DefaultAPIHandler from 'Utils/DefaultAPIHandler'
import OpenLoginDialog from 'Utils/OpenLoginDialog'
import TinymceContentViewer from 'Components/TinymceContentViewer'
import PageHead from 'Components/PageHead'
import moduleService from 'Services/moduleService'
import Lazyload from 'Components/Lazyload'
import LandingPageOverviewPanel from 'Components/LandingPageOverviewPanel'
import customClasses from './index.module.css'

const CourseTitle = ({ course }) => (
  <p className="text-gray-800 font-semibold text-xl md:text-2xl tracking-wide mb-2">
    {course.title}
  </p>
)

CourseTitle.propTypes = {
  course: PropTypes.object.isRequired,
}

let loadingMoreModules = false

const AddEditReviewModal = Lazyload(() =>
  import('Components/AddEditReviewModal')
)
const ModuleBlockForLanding = Lazyload(
  () => import('Components/ModuleBlockForLanding'),
  () => <div style={{ height: '50px' }} />
) // to avoid flicker

const PurchaseCourseModal = Lazyload(() =>
  import('Components/PurchaseCourseModal')
)

class CourseLandingPage extends Component {
  static async getInitialProps({ query }) {
    const courseSlug = query.slug
    return InitialPropsHelper({
      course: courseService.getLandingPageCourse(courseSlug),
      modulesData: moduleService.getLandingPageModules(courseSlug),
      contentStats: courseService.getContentStats(courseSlug),
    })
  }

  constructor(props) {
    super(props)
    const { course, modulesData, router } = props
    let seoDescription = ''
    if (course && course.description) {
      seoDescription = course.description.replace(/(<([^>]+)>)/gi, '')
    }
    this.state = {
      course,
      initJoin: router.query.join,
      initTrialJoin: router.query.trialJoin,
      initDiscountCode: router.query.discountCode,
      loggedUser: globalsService.getLoggedUser(),
      reviewsData: null,
      modulesData,
      joiningInProgress: false,
      trialJoiningInProgress: false,
      reviewModalOpen: false,
      purchaseCourseModalOpen: false,
      showReviews: false,
      panelFixed: false,
      seoDescription,
    }
    this.addToCourse = this.addToCourse.bind(this)
    this.joinCourse = this.joinCourse.bind(this)
    this.getReviews = this.getReviews.bind(this)
    this.goToCourse = this.goToCourse.bind(this)
    this.joinTrialClicked = this.joinTrialClicked.bind(this)
    this.joinTrial = this.joinTrial.bind(this)
    this.handleReviewModalClose = this.handleReviewModalClose.bind(this)
    this.loadMoreModules = this.loadMoreModules.bind(this)
    this.handlePurchaseModalClose = this.handlePurchaseModalClose.bind(this)
  }

  componentDidMount() {
    const { initJoin, initTrialJoin, course } = this.state
    if (initJoin) this.joinCourse()
    else if (initTrialJoin) this.joinTrialClicked()
    if (course.reviewsEnabled) this.getReviews()
    window.addEventListener('scroll', this.listenScrollEvent)
  }

  getReviews(page) {
    const { course } = this.state
    courseService.getReviews(course.slug, page).then(
      (data) => {
        this.setState({ reviewsData: data })
      },
      () => {}
    )
  }

  listenScrollEvent = () => {
    if (window.scrollY > 128) {
      this.setState({ panelFixed: true })
    } else {
      this.setState({ panelFixed: false })
    }
  }

  joinCourse() {
    const { initDiscountCode, course, loggedUser } = this.state
    if (!loggedUser) {
      let redirectUrl = `/course/${course.slug}?join=true`
      if (initDiscountCode) redirectUrl += `&discountCode=${initDiscountCode}`
      OpenLoginDialog(redirectUrl)
    } else if (course.roles.isMember && course.plan === 'public') {
      // member of public course, go to course
      this.goToCourse()
    } else if (course.roles.isMember && !course.trialData) {
      // member of paid course and no trial enabled
      this.goToCourse()
    } else if (course.plan === 'public') {
      this.addToCourse({})
    } else if (course.plan === 'paid') {
      this.setState({ purchaseCourseModalOpen: true })
    }
  }

  joinTrialClicked() {
    const { course, loggedUser } = this.state
    // run validation because of `initTrialJoin` flag
    if (!course.trialEnabled) return
    const u = loggedUser
    if (!u) {
      OpenLoginDialog(`/course/${course.slug}?trialJoin=true`)
    } else if (course.roles.isMember) {
      this.goToCourse()
    } else if (course.plan === 'paid') {
      this.joinTrial()
    }
  }

  joinTrial() {
    const { course } = this.state
    this.setState({ trialJoiningInProgress: true })
    DefaultAPIHandler(courseService.startFreeTrial(course.id))
      .then(
        () => {
          // clear cache incase of trial to full course conversion
          courseService.getCourseContent(course.slug, true)
          this.goToCourse()
        },
        () => {}
      )
      .finally(() => this.setState({ trialJoiningInProgress: false }))
  }

  addToCourse(inputData) {
    const { course } = this.state
    this.setState({ joiningInProgress: true })
    DefaultAPIHandler(courseService.joinCourse(course.id, inputData))
      .then(
        () => {
          // clear cache incase of trial to full course conversion
          courseService.getCourseContent(course.slug, true)
          this.goToCourse()
        },
        () => {}
      )
      .finally(() => this.setState({ joiningInProgress: false }))
  }

  goToCourse() {
    const { course } = this.state
    Router.push(
      {
        pathname: '/course/[slug]/content',
        query: { slug: course.slug },
      },
      `/course/${course.slug}/content`
    )
  }

  handleReviewModalClose(course) {
    this.setState({ reviewModalOpen: false })
    if (course) {
      this.setState({ course })
      this.getReviews()
    }
  }

  loadMoreModules() {
    const { course, modulesData } = this.state
    if (!loadingMoreModules) {
      loadingMoreModules = true
      const currentPage = modulesData.p.page
      DefaultAPIHandler(
        moduleService.getLandingPageModules(course.slug, currentPage + 1)
      )
        .then(
          (data) => {
            this.setState((prevState) => {
              const newList = [...prevState.modulesData.result, ...data.result]
              const newModulesData = data
              newModulesData.result = newList
              return { modulesData: newModulesData }
            })
          },
          () => {}
        )
        .finally(() => {
          loadingMoreModules = false
        })
    }
  }

  handlePurchaseModalClose(orderData) {
    const { loggedUser, course } = this.state
    this.setState({ purchaseCourseModalOpen: false })
    if (orderData && orderData.result) {
      paymentService.makePayment(
        {
          name: course.title,
          order_id: orderData.result,
          description: orderData.description,
          prefill: {
            email: loggedUser.email,
            name: loggedUser.name,
            contact: loggedUser.mobileNumber,
          },
        },
        (razorpayResponseObject) => {
          DefaultAPIHandler(
            essentialsService.finishPayment(razorpayResponseObject)
          ).then(
            () => {
              courseService.getCourseContent(course.slug, true)
              this.goToCourse()
            },
            () => {}
          )
        }
      )
    }
  }

  render() {
    const { contentStats } = this.props
    const {
      course,
      reviewModalOpen,
      joiningInProgress,
      trialJoiningInProgress,
      modulesData,
      reviewsData,
      seoDescription,
      purchaseCourseModalOpen,
      initDiscountCode,
      showReviews,
      panelFixed,
    } = this.state

    return (
      <div className="overflow-hidden block md:inline-flex w-full py-0 px-4">
        <div className="md:hidden">
          <div className="bg-white mb-6 p-4 md:p-8 rounded-xl min-h-full">
            <CourseTitle course={course} />
            {course.ratingData && course.ratingData.rating > 0 && (
              <div className="mb-2">
                <RatingBlock
                  rating={course.ratingData.rating}
                  nRatings={course.ratingData.nRatings}
                  size="large"
                />
              </div>
            )}
            <LandingPageOverviewPanel
              contentStats={contentStats}
              joiningInProgress={joiningInProgress}
              trialJoiningInProgress={trialJoiningInProgress}
              goToCourse={this.goToCourse}
              course={course}
              joinCourse={this.joinCourse}
              joinTrialClicked={this.joinTrialClicked}
            />
          </div>
        </div>
        <div
          className={`${customClasses.leftContainer} p-4 md:p-8 bg-white rounded-xl min-h-full mb-4`}
        >
          <PageHead
            title={course.title}
            description={seoDescription}
            card={{
              title: course.title,
              type: 'website',
              url: course.permalink,
              // use exact=true in GetSchoolUrl
              image: course.thumbnail,
            }}
          />
          <div className="hidden md:block">
            <CourseTitle course={course} />
            {course.ratingData && course.ratingData.rating > 0 && (
              <div className="mb-2">
                <RatingBlock
                  rating={course.ratingData.rating}
                  nRatings={course.ratingData.nRatings}
                  size="large"
                />
              </div>
            )}
          </div>
          {course.description && (
            <div className="mx-0 mt-4 mb-8 text-lg">
              <TinymceContentViewer content={course.description} />
            </div>
          )}
          <div style={{ marginTop: '40px' }}>
            {modulesData.result.map((m) => (
              <ModuleBlockForLanding module={m} key={m.id} />
            ))}
            {modulesData.p.hasNext && (
              <div>
                <button
                  type="button"
                  onClick={this.loadMoreModules}
                  className="mt-2 text-blue-600 text-base"
                >
                  Load more modules
                </button>
              </div>
            )}
            {modulesData.result.length === 0 && (
              <AlertBlock variant="warning" showIcon>
                No modules visible
              </AlertBlock>
            )}
          </div>
          {/* Review */}
          {course.reviewsEnabled && reviewsData != null && (
            <div style={{ marginTop: '16px' }}>
              <div style={{ display: 'flow-root' }}>
                <Button
                  color="secondary"
                  onClick={() => this.setState({ showReviews: !showReviews })}
                >
                  {showReviews ? 'Hide Reviews' : 'Show Reviews'}
                </Button>
                {course.roles.isStudent && showReviews && (
                  <Button
                    color="primary"
                    size="small"
                    style={{ float: 'right' }}
                    onClick={() => this.setState({ reviewModalOpen: true })}
                  >
                    {`${course.review ? 'EDIT YOUR REVIEW' : 'WRITE A REVIEW'}`}
                  </Button>
                )}
              </div>
              {showReviews && (
                <div>
                  <div style={{ marginTop: '16px' }}>
                    {reviewsData.result.length === 0 && (
                      <AlertBlock variant="warning" showIcon>
                        No reviews found
                      </AlertBlock>
                    )}
                    {reviewsData.result.map((r) => (
                      <div key={r.id} style={{ marginBottom: '16px' }}>
                        <CourseReview review={r} />
                      </div>
                    ))}
                  </div>
                  <AddEditReviewModal
                    course={course}
                    open={reviewModalOpen}
                    onClose={this.handleReviewModalClose}
                  />
                </div>
              )}
            </div>
          )}
          <div style={{ height: '200px' }} />
          <PurchaseCourseModal
            initDiscountCode={initDiscountCode}
            open={purchaseCourseModalOpen}
            course={course}
            onClose={this.handlePurchaseModalClose}
          />
        </div>
        <div
          className={`hidden md:block shadow-xl rounded-xl w-80  lg:w-96 bg-white p-4 md:p-8 h-full ${
            panelFixed ? customClasses.fixedPanel : ''
          }`}
        >
          <LandingPageOverviewPanel
            contentStats={contentStats}
            joiningInProgress={joiningInProgress}
            trialJoiningInProgress={trialJoiningInProgress}
            goToCourse={this.goToCourse}
            course={course}
            joinCourse={this.joinCourse}
            joinTrialClicked={this.joinTrialClicked}
          />
        </div>
      </div>
    )
  }
}

CourseLandingPage.propTypes = {
  modulesData: PropTypes.object.isRequired,
  contentStats: PropTypes.object.isRequired,
  course: PropTypes.object.isRequired,
  router: PropTypes.object.isRequired,
}

// withrouter wrap the layout component not inside component (will mess up get initial props)

export default withRouter(
  Layout(CourseLandingPage, {
    sidebarVariant: 'NONE',
    isFooterPresent: false,
  })
)
